import {Component, EventEmitter, Input, Output, inject} from '@angular/core';
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {NgOptimizedImage} from "@angular/common";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {DocumentService} from "../../../shared/services/document.service";
import {DialogUtility} from "@syncfusion/ej2-popups";
import {Dialog} from "@syncfusion/ej2-angular-popups";
import {LanguageService} from "../../../shared/services/language.service";
import {FileStorageResource} from '../../../shared/types/generated/fileStorageResource';
import {NgSrcImageErrorDirective} from '../../../shared/directives/ng-src-image-error.directive';
import {take} from 'rxjs';
import {DocumentsRemoveFileHook} from '../documents-upload-hooks.type';

@Component({
    selector: 'app-item-documents-preview-selected',
    imports: [
        IconBadgeComponent,
        NgOptimizedImage,
        TooltipComponent,
        NgSrcImageErrorDirective
    ],
    templateUrl: './item-documents-preview-selected.component.html',
    styleUrl: './item-documents-preview-selected.component.scss'
})
export class ItemDocumentsPreviewSelectedComponent {
  public documentService: DocumentService = inject(DocumentService);
  private languageService: LanguageService = inject(LanguageService);


  @Input() public file: FileStorageResource | null = null;
  @Input() public files: FileStorageResource[] = [];
  @Input() public position: number = 0;
  @Input() public removeBadge: 'trash' | 'unselect' = 'trash';
  @Input() public disabled: boolean = false;
  @Input() public removeFileFn?: DocumentsRemoveFileHook;
  @Output() public  filesChange: EventEmitter<FileStorageResource[]> = new EventEmitter<FileStorageResource[]>();

  private dialogInstance: Dialog | null = null;
  private removalInProgress: boolean = false;
  private dialogTexts:{confirm: string, sure: string, okay: string} = {
    confirm: '',
    sure: '',
    okay: '',
  }

  public openGallery(event: MouseEvent, document: FileStorageResource): void {
    event.stopPropagation();
    this.documentService.showGallery(this.files, document);
  }

  public opensDocument(document: FileStorageResource): boolean {
    return document.type !== 'image' && document.type !== 'video';
  }

  public opensGallery(document: FileStorageResource): boolean {
    return document.type === 'image' || document.type === 'video';
  }

  public hasTileAction(document: FileStorageResource): boolean {
    return this.opensDocument(document) || this.opensGallery(document);
  }

  public onTileClick(document: FileStorageResource): void {
    if (this.opensGallery(document)) {
      this.documentService.showGallery(this.files, document);
      return;
    }

    if (this.opensDocument(document)) {
      this.documentService.openDocument(document);
    }
  }

  public downloadDocumentFromAction(event: MouseEvent, document: FileStorageResource): void {
    event.stopPropagation();
    this.documentService.downloadDocument(document);
  }

  public onTileKeydown(event: KeyboardEvent, document: FileStorageResource): void {
    if (event.target !== event.currentTarget || !this.hasTileAction(document) || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    if (event.key === ' ') {
      event.preventDefault();
    }

    this.onTileClick(document);
  }

  public moveFile(fileToMove: FileStorageResource, direction: 'up' | 'down'): void {
    if (this.disabled) {
      return;
    }

    const index = this.getFileIndex(fileToMove);
    if (index < 0) {
      return;
    }

    const file = this.files[index];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= this.files.length) {
      return;
    }

    this.files[index] = this.files[newIndex];
    this.files[newIndex] = file;
    this.filesChange.emit([...this.files]);
  }

  public showDeleteOverlay(fileToDelete: FileStorageResource): void {
    if (this.disabled) {
      return;
    }

    this.setDialogTexts();
    this.dialogInstance = DialogUtility.confirm({
      title: '<h6 class="text-danger fw-bold m-0 pt-1">' + this.dialogTexts.confirm + '</h6>',
      content: this.dialogTexts.sure,
      cancelButton: {
        text: this.languageService.getTranslationByKey('CANCEL'),
        click: () => this.showDeleteOverlayCancel()
      },
      okButton: {
        text: this.dialogTexts.okay,
        click: () => this.showDeleteOverlayConfirmed(fileToDelete),
        cssClass: 'bg-danger brd-danger'
      },
      showCloseIcon: true,
      closeOnEscape: true,
      position: {X: 'center', Y: 'center'},
      animationSettings: {effect: 'Fade'},
      zIndex: 9999
    });
  }

  private showDeleteOverlayConfirmed(fileToDelete: FileStorageResource): void {
    if (this.removalInProgress) {
      return;
    }

    const index = this.getFileIndex(fileToDelete);
    if (index < 0) {
      this.dialogInstance?.hide();
      return;
    }

    if (this.removeFileFn === undefined) {
      this.files.splice(index, 1);
      this.filesChange.emit([...this.files]);
      this.dialogInstance?.hide();
      return;
    }

    this.removalInProgress = true;
    this.removeFileFn(fileToDelete, [...this.files]).pipe(take(1)).subscribe({
      next: (nextFiles) => {
        this.files = nextFiles;
        this.filesChange.emit([...this.files]);
        this.removalInProgress = false;
        this.dialogInstance?.hide();
      },
      error: (error: unknown) => {
        this.removalInProgress = false;
        this.dialogInstance?.hide();
        console.error(error);
      },
    });
  }

  private showDeleteOverlayCancel(): void {
    this.dialogInstance?.hide();
  }

  private setDialogTexts(): void {
    if(this.removeBadge === 'trash') {
      this.dialogTexts.confirm = this.languageService.getTranslationByKey('CONFIRMITEMDELETE');
      this.dialogTexts.sure = this.languageService.getTranslationByKey('AREYOUSUREYOUWANTTODELETE');
      this.dialogTexts.okay = this.languageService.getTranslationByKey('DELETE');
    } else {
      this.dialogTexts.confirm = this.languageService.getTranslationByKey('CONFIRMITEMUNSELECT');
      this.dialogTexts.sure = this.languageService.getTranslationByKey('AREYOUSUREYOUWANTTOUNSELECT');
      this.dialogTexts.okay = this.languageService.getTranslationByKey('UNSELECT');
    }
  }

  private getFileIndex(file: FileStorageResource): number {
    const id = file.uuid;
    if (id) {
      const byId = this.files.findIndex((item) => item.uuid === id);
      if (byId >= 0) {
        return byId;
      }
    }

    return this.files.indexOf(file);
  }
}
